import { useMemo, useState } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import type { CampaignMediaItem } from '@/features/campaigns/api/campaignDetailApi';
import type { CreativePreview } from '@/features/campaigns/types/campaign';
import {
  ExactScreenFrame,
  resolvePreviewDevice,
  resolvePreviewObjectFit,
  normalizeDetectedOrientation,
  type PreviewDeviceKind,
} from '@/features/campaigns/components/ManualBuilder';

type CampaignCreativePreviewProps = {
  media: CampaignMediaItem[];
};

const DEVICE_TABS: {
  id: PreviewDeviceKind;
  label: string;
  short: string;
  Icon: typeof Monitor;
}[] = [
  { id: 'landscape', label: 'Landscape', short: '16:9', Icon: Monitor },
  { id: 'portrait', label: 'Portrait', short: '9:16', Icon: Smartphone },
];

function toCreative(item?: CampaignMediaItem): CreativePreview | undefined {
  if (!item?.mediaUrl) return undefined;
  return {
    url: item.mediaUrl,
    type: item.mediaType || 'image',
  };
}

export function CampaignCreativePreview({ media }: CampaignCreativePreviewProps) {
  const items = media.filter((m) => Boolean(m.mediaUrl));
  const [activeIndex, setActiveIndex] = useState(0);
  const active = items[Math.min(activeIndex, Math.max(items.length - 1, 0))];

  const creativeOrientation = normalizeDetectedOrientation(active?.orientation);
  const suggestedDevice = resolvePreviewDevice({
    creativeOrientation,
  });
  const [deviceOverride, setDeviceOverride] = useState<PreviewDeviceKind | null>(
    null,
  );

  // Follow creative when index/orientation changes
  const device = deviceOverride ?? suggestedDevice;
  const creative = useMemo(() => toCreative(active), [active]);
  const objectFit = resolvePreviewObjectFit(creativeOrientation, device);

  if (!items.length) {
    return (
      <div className="flex min-h-[280px] items-center justify-center rounded-[22px] border border-dashed border-black/[0.08] bg-[#F7F8FA]">
        <p className="m-0 text-sm font-medium text-[var(--dash-muted)]">
          No creative attached
        </p>
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-[22px] border border-black/[0.06] bg-white shadow-[0_8px_30px_-18px_rgba(15,23,42,0.18)]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/[0.05] px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <p className="m-0 text-[15px] font-semibold tracking-[-0.02em] text-[var(--dash-ink)]">
            Live display
          </p>
          <p className="m-0 mt-0.5 text-[12px] text-[var(--dash-muted)]">
            Same Screenista frames used in campaign builder
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Preview device"
          className="grid grid-cols-2 gap-1 rounded-[12px] bg-[#F2F4F7] p-1"
        >
          {DEVICE_TABS.map(({ id, label, short, Icon }) => {
            const selected = device === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setDeviceOverride(id)}
                className={`inline-flex items-center justify-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-[12px] font-semibold transition ${
                  selected
                    ? 'bg-white text-[var(--dash-ink)] shadow-[0_1px_3px_rgba(15,23,42,0.08)]'
                    : 'text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
                }`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                <span>{label}</span>
                <span className="hidden text-[10px] font-medium opacity-60 sm:inline">
                  {short}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative flex min-h-[320px] items-center justify-center bg-[linear-gradient(180deg,#F7F8FA_0%,#EEF1F5_100%)] px-4 py-6 sm:min-h-[380px] sm:px-6">
        <div className="h-[300px] w-full max-w-[520px] sm:h-[340px]">
          <ExactScreenFrame
            device={device}
            creative={creative}
            objectFit={objectFit}
          />
        </div>

        {creativeOrientation === 'PORTRAIT' ||
        creativeOrientation === 'LANDSCAPE' ? (
          <span className="absolute top-4 left-4 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold tracking-wide text-white backdrop-blur-sm">
            {creativeOrientation} creative
          </span>
        ) : null}
      </div>

      {items.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto border-t border-black/[0.05] px-4 py-3 sm:px-5">
          {items.map((item, index) => {
            const selected = index === activeIndex;
            const isVideo = item.mediaType?.startsWith('video');
            return (
              <button
                key={`${item.mediaId || item.mediaUrl}-${index}`}
                type="button"
                onClick={() => {
                  setActiveIndex(index);
                  setDeviceOverride(null);
                }}
                className={`relative h-14 w-20 shrink-0 overflow-hidden rounded-[12px] border transition ${
                  selected
                    ? 'border-[var(--dash-brand)] ring-2 ring-[var(--dash-brand)]/25'
                    : 'border-black/[0.06] hover:border-black/[0.12]'
                }`}
                aria-label={`Creative ${index + 1}`}
              >
                {isVideo ? (
                  <video
                    src={item.mediaUrl}
                    className="h-full w-full object-cover"
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={item.mediaUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                )}
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
